import React, { useEffect, useState } from 'react';
import {
  Bookmark,
  Briefcase,
  ChevronDown,
  ChevronRight,
  Globe,
  Heart,
  Loader2,
  MapPin,
  UserRound,
  Users,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/badge';
import { DEFAULT_RCC_THEME, type RccTheme } from './rcc-themes';
import { RccActiveFilterChips, RccFilterMenu, countActiveFilters } from './RccFilters';
import { RccCandidateExpand } from './RccCandidateExpand';
import { getCandidateDisplayTitle } from './rccDisplay';
import type { AiResultMap, RccBoardProps, RccCandidate, RccHandlers, RccJobColumn, TFn } from './rccTypes';

const theme: RccTheme = DEFAULT_RCC_THEME;

function getFiltered(col: RccJobColumn): RccCandidate[] {
  if (col.languageFilter) {
    return col.candidates.filter(
      (c) => (c.cv_language || '').toUpperCase() === col.languageFilter,
    );
  }
  return col.candidates;
}

function StatsStrip({ items }: { items: { value: number; label: string }[] }) {
  return (
    <div
      className={cn(
        'mb-6 grid grid-cols-2 overflow-hidden rounded-xl border shadow-sm lg:grid-cols-4',
        theme.border,
        theme.surface,
      )}
    >
      {items.map((item, i) => (
        <div
          key={item.label}
          className={cn(
            'px-4 py-4 text-center',
            i > 0 && `lg:border-l ${theme.borderSubtle}`,
            i >= 2 && `border-t lg:border-t-0 ${theme.borderSubtle}`,
          )}
        >
          <div className="text-2xl font-bold tabular-nums tracking-tight text-slate-900">
            {item.value}
          </div>
          <div className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
            {item.label}
          </div>
        </div>
      ))}
    </div>
  );
}

function QueueStatusLegend({ t }: { t: TFn }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
      <span className="inline-flex items-center gap-1.5">
        <span className="size-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
        {t('command_center.status_contacted')}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Heart className="size-3 fill-pink-500 text-pink-500" aria-hidden />
        {t('card.interested')}
      </span>
    </div>
  );
}

function QueueRow({
  candidate: c,
  col,
  t,
  h,
  aiResults,
  aiLoading,
  companyId,
  renderAtsButton,
  jobSavesVersion,
}: {
  candidate: RccCandidate;
  col: RccJobColumn;
  t: TFn;
  h: RccHandlers;
  aiResults: AiResultMap;
  aiLoading: Record<string, boolean>;
  companyId: string | null;
  renderAtsButton: (candidate: RccCandidate) => React.ReactNode;
  jobSavesVersion: number;
}) {
  const [expanded, setExpanded] = useState(false);
  void jobSavesVersion;
  const saved = h.getJobSaves(c.cv_id).includes(col.title);
  const contacted = !!c.reach_out_id;
  const { title: displayTitle, source: titleSource } = getCandidateDisplayTitle(c, t);

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className={cn('flex w-full items-start gap-3 px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-300', theme.surfaceHover)}
      >
        <span className="relative flex size-8 shrink-0 items-center justify-center self-center rounded-full bg-slate-100">
          <UserRound className="size-4 text-slate-400" aria-hidden />
          {contacted && (
            <span
              className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2 border-white bg-emerald-500"
              title={t('command_center.status_contacted')}
              aria-hidden
            />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex min-h-5 items-center gap-1.5">
            <span className="flex min-w-0 items-center gap-1.5">
              <span
                className={cn(
                  'truncate text-sm font-semibold leading-none',
                  titleSource === 'current_role' ? 'text-slate-900' : 'text-slate-700',
                )}
              >
                {titleSource === 'preview' && (
                  <span className="font-medium text-slate-500">{t('card.from_cv')} · </span>
                )}
                {titleSource === 'role_preferences' && (
                  <span className="font-medium text-slate-500">{t('card.seeking')} · </span>
                )}
                {displayTitle}
              </span>
              {c.is_interested && (
                <Heart
                  className="size-3.5 shrink-0 fill-pink-500 text-pink-500"
                  aria-label={t('card.interested')}
                />
              )}
            </span>
          </div>
          <div className="mt-1.5 space-y-1 text-[11px] leading-snug text-slate-600">
            {c.base_locations && (
              <p className="flex min-w-0 items-start gap-1.5">
                <MapPin className="mt-0.5 size-3 shrink-0 text-slate-400" aria-hidden />
                <span className="min-w-0">
                  <span className="font-medium text-slate-400">{t('card.base_location')}</span>
                  {' · '}
                  {c.base_locations}
                </span>
              </p>
            )}
            {c.role_preferences && titleSource !== 'role_preferences' && (
              <p className="flex min-w-0 items-start gap-1.5">
                <Briefcase className="mt-0.5 size-3 shrink-0 text-slate-400" aria-hidden />
                <span className="min-w-0 line-clamp-2">
                  <span className="font-medium text-slate-400">{t('card.seeking')}</span>
                  {' · '}
                  {c.role_preferences}
                </span>
              </p>
            )}
            {c.preferred_locations && (
              <p className="flex min-w-0 items-start gap-1.5">
                <Globe className="mt-0.5 size-3 shrink-0 text-slate-400" aria-hidden />
                <span className="min-w-0">
                  <span className="font-medium text-slate-400">{t('card.preferred_location')}</span>
                  {' · '}
                  {c.preferred_locations}
                </span>
              </p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 self-start pt-0.5">
          {c.current_level && (
            <Badge variant="secondary" className="shrink-0 text-[10px]">
              {c.current_level}
            </Badge>
          )}
          {c.cv_language && (
            <span
              className={cn(
                'inline-flex h-6 min-w-[2.25rem] shrink-0 items-center justify-center rounded border px-1 text-xs font-semibold uppercase leading-none tracking-wide text-slate-600',
                theme.chipBorder,
              )}
            >
              {c.cv_language.toUpperCase()}
            </span>
          )}
          <span className="flex size-3 shrink-0 items-center justify-center" aria-hidden={!saved}>
            {saved && (
              <Bookmark className="size-3 fill-pink-500 text-pink-500" aria-label={t('card.saved')} />
            )}
          </span>
          <ChevronDown
            className={`size-4 shrink-0 text-slate-400 transition-transform duration-200 ${
              expanded ? 'rotate-180' : ''
            }`}
            aria-hidden
          />
        </div>
      </button>

      {expanded && (
        <div className={cn('border-t', theme.borderSubtle, theme.surfaceMuted)}>
          <div className="flex gap-3 px-4 py-3">
            <span className="w-4 shrink-0" aria-hidden />
            <div className={cn('min-w-0 flex-1 rounded-lg border p-2.5 shadow-sm', theme.border, theme.surface)}>
              <RccCandidateExpand
                candidate={c}
                col={col}
                t={t}
                h={h}
                aiResults={aiResults}
                aiLoading={aiLoading}
                companyId={companyId}
                renderAtsButton={renderAtsButton}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function RecruiterCommandCenterBoard(props: RccBoardProps) {
  const {
    t,
    columns,
    companyId,
    interestedCount,
    aiResults,
    aiLoading,
    jobSavesVersion,
    renderAtsButton,
    onSelectJob,
    ...handlers
  } = props;

  const h: RccHandlers = { onSelectJob, ...handlers };

  const [activeJobId, setActiveJobId] = useState<string>(columns[0]?.id ?? '');
  const activeCol = columns.find((c) => c.id === activeJobId) ?? columns[0];

  // Lazy-load the active column's candidates via the existing load path.
  useEffect(() => {
    if (activeCol) onSelectJob(activeCol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCol?.id, activeCol?.loaded, activeCol?.loading]);

  if (!activeCol) return null;

  const candidatesMatched = columns
    .filter((c) => c.loaded)
    .reduce((s, c) => s + c.totalLoaded, 0);
  void jobSavesVersion;
  const savedTotal = columns.reduce(
    (s, c) => s + c.candidates.filter((cand) => h.getJobSaves(cand.cv_id).includes(c.title)).length,
    0,
  );

  const filtered = getFiltered(activeCol);
  const filterCount = countActiveFilters(activeCol);

  let listState: 'loading' | 'empty' | 'no_lang' | 'list';
  if (activeCol.loading || !activeCol.loaded) listState = 'loading';
  else if (filtered.length === 0) listState = activeCol.languageFilter ? 'no_lang' : 'empty';
  else listState = 'list';

  return (
    <>
      <StatsStrip
        items={[
          { value: columns.length, label: t('stats.open_positions') },
          { value: candidatesMatched, label: t('stats.candidates_matched') },
          { value: interestedCount, label: t('stats.interested') },
          { value: savedTotal, label: t('stats.saved') },
        ]}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
        {/* Role queue side column */}
        <aside className="space-y-2">
          <div className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('command_center.open_roles')}
          </div>
          {columns.map((col) => {
            const active = col.id === activeJobId;
            const count = getFiltered(col).length;
            return (
              <button
                key={col.id}
                type="button"
                onClick={() => setActiveJobId(col.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-xl border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1',
                  active
                    ? cn(theme.borderActive, theme.surfaceMuted)
                    : cn(theme.border, theme.surface, theme.surfaceHover),
                )}
              >
                <Briefcase
                  className={cn('size-4 shrink-0', active ? 'text-slate-500' : 'text-slate-300')}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-900">
                    {col.role || col.title}
                  </span>
                  {col.activeLocations.length > 0 && (
                    <span className="block truncate text-xs text-slate-500">
                      {col.activeLocations.slice(0, 2).join(', ')}
                      {col.activeLocations.length > 2 ? ` +${col.activeLocations.length - 2}` : ''}
                    </span>
                  )}
                </span>
                {col.loaded && (
                  <Badge variant="outline" className="text-[10px] tabular-nums">
                    {count}
                  </Badge>
                )}
                <ChevronRight className={`size-4 ${active ? 'text-slate-600' : 'text-slate-300'}`} />
              </button>
            );
          })}
        </aside>

        {/* Active role queue */}
        <div className="min-w-0 space-y-3">
          <div className={cn('overflow-visible rounded-xl border px-4 py-4 shadow-sm', theme.border, theme.surface)}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="truncate text-lg font-bold tracking-tight text-slate-900">
                  {activeCol.role || activeCol.title}
                </h2>
                {activeCol.activeLocations.length > 0 && (
                  <p className="mt-0.5 truncate text-sm text-slate-500">
                    {activeCol.activeLocations.join(', ')}
                  </p>
                )}
              </div>
              <Badge
                variant="outline"
                className={cn('shrink-0 gap-2 px-3 py-1.5 text-base font-medium tabular-nums', theme.border)}
              >
                <Users className="size-4" />
                {filtered.length}
              </Badge>
            </div>
            <div className="mt-2.5">
              <QueueStatusLegend t={t} />
            </div>
          </div>

          <div className={cn('rounded-xl border shadow-sm', theme.border, theme.surface)}>
            <div
              className={cn(
                'flex flex-wrap items-center justify-between gap-3 overflow-visible rounded-t-xl border-b px-4 py-2.5',
                theme.border,
                theme.surfaceMuted,
              )}
            >
              <div className="min-w-0 flex-1">
                <RccActiveFilterChips col={activeCol} theme={theme} h={h} />
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {filterCount > 0 && (
                  <button
                    type="button"
                    onClick={() => h.clearAllFilters(activeCol.id)}
                    className="rounded-md px-1.5 py-1 text-xs font-medium text-slate-500 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
                  >
                    {t('command_center.clear_all')}
                  </button>
                )}
                <RccFilterMenu col={activeCol} filterCount={filterCount} theme={theme} t={t} h={h} />
              </div>
            </div>

            <div className="overflow-hidden rounded-b-xl">
              {listState === 'loading' && (
                <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
                  <Loader2 className="size-4 animate-spin text-slate-500" />
                  {t('column.searching')}
                </div>
              )}
              {listState === 'empty' && (
                <p className="py-16 text-center text-sm text-slate-400">{t('column.no_candidates')}</p>
              )}
              {listState === 'no_lang' && (
                <p className="py-16 text-center text-sm text-slate-400">
                  {t('column.no_lang_candidates', { lang: activeCol.languageFilter })}
                </p>
              )}
              {listState === 'list' && (
                <div className={cn('divide-y', theme.divide)}>
                  {filtered.map((c) => (
                    <QueueRow
                      key={c.cv_id}
                      candidate={c}
                      col={activeCol}
                      t={t}
                      h={h}
                      aiResults={aiResults}
                      aiLoading={aiLoading}
                      companyId={companyId}
                      renderAtsButton={renderAtsButton}
                      jobSavesVersion={jobSavesVersion}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
